import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { IdCard, Plus, Trash2, Save, Loader2, Edit3, Phone, Mail, MapPin, Compass, KeyRound, ChevronsUpDown, X } from "lucide-react";
import { ITALIAN_CITIES } from "@/lib/cities";

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium">{value || "—"}</div>
  </div>
);

const ANA_FIELDS = [
  ["name", "Ragione sociale"], ["partita_iva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["pec", "PEC"], ["codice_sdi", "Codice SDI"], ["iban", "IBAN"],
  ["sede_legale_address", "Indirizzo sede legale"], ["sede_legale_cap", "CAP"],
  ["sede_legale_city", "Città sede legale"], ["sede_legale_prov", "Prov."],
  ["phone", "Telefono"], ["email", "Email"], ["referente", "Referente principale (Agente)"],
];

export default function AgencyAnagrafica({ agency, agencies = [], canEdit, open, setOpen, onSaved }) {
  const [form, setForm] = useState(agency);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [provincesByRegion, setProvincesByRegion] = useState({});
  const [provOpen, setProvOpen] = useState(false);

  useEffect(() => { if (open) setForm({ ...agency, referenti: agency.referenti || [], regions: agency.regions || [], provinces: agency.provinces || [] }); }, [open, agency]);
  useEffect(() => {
    if (!open || regions.length) return;
    Promise.all([api.get("/regions"), api.get("/provinces")])
      .then(([r, pv]) => { setRegions(r.data || []); setProvincesByRegion(pv.data || {}); })
      .catch(() => {});
  }, [open, regions.length]);

  const assignedRegionsOther = new Set(agencies.filter((a) => a.id !== agency.id).flatMap((a) => a.regions || []));
  const assignedProvincesOther = new Set(agencies.filter((a) => a.id !== agency.id).flatMap((a) => a.provinces || []));

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setSede = (cityName) => { const c = ITALIAN_CITIES.find((x) => x.name === cityName); if (c) setForm((s) => ({ ...s, sede_city: c.name, lat: c.lat, lng: c.lng })); };
  const addRegion = (r) => setForm((s) => ((s.regions || []).includes(r) ? s : { ...s, regions: [...(s.regions || []), r] }));
  const removeRegion = (r) => setForm((s) => ({ ...s, regions: (s.regions || []).filter((x) => x !== r) }));
  const addProvince = (p) => setForm((s) => ((s.provinces || []).includes(p) ? s : { ...s, provinces: [...(s.provinces || []), p] }));
  const removeProvince = (p) => setForm((s) => ({ ...s, provinces: (s.provinces || []).filter((x) => x !== p) }));
  const setRef = (i, k, v) => setForm((s) => { const r = [...(s.referenti || [])]; r[i] = { ...r[i], [k]: v }; return { ...s, referenti: r }; });
  const addRef = () => setForm((s) => ({ ...s, referenti: [...(s.referenti || []), { nome: "", ruolo: "", telefono: "", email: "" }] }));
  const delRef = (i) => setForm((s) => ({ ...s, referenti: (s.referenti || []).filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = { ...agency, ...form };
      delete payload.id; delete payload.agency_contract; delete payload.agency_contracts;
      delete payload.relazione; delete payload.created_at;
      await api.put(`/agencies/${agency.id}`, payload);
      toast.success("Anagrafica aggiornata");
      setOpen(false); onSaved?.();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const refs = agency.referenti || [];
  const sedeLegale = [agency.sede_legale_address, agency.sede_legale_cap, agency.sede_legale_city, agency.sede_legale_prov].filter(Boolean).join(" ");
  return (
    <Card data-testid="agency-anagrafica-section" className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold tracking-tight sm:text-lg"><IdCard className="h-5 w-5 text-primary" />Dettaglio anagrafica</h2>
        {canEdit && <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid="edit-anagrafica-inline"><Edit3 className="mr-1.5 h-4 w-4" />Modifica</Button>}
      </div>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Ragione sociale" value={agency.name} />
        <Field label="Agente / Referente" value={agency.referente} />
        <Field label="Partita IVA" value={agency.partita_iva} />
        <Field label="Codice fiscale" value={agency.codice_fiscale} />
        <Field label="PEC" value={agency.pec} />
        <Field label="Codice SDI" value={agency.codice_sdi} />
        <Field label="IBAN" value={agency.iban} />
        <Field label="Sede legale" value={sedeLegale} />
        <Field label="Sede (città)" value={agency.sede_city} />
        <Field label="Sede operativa" value={agency.sede_op_address} />
        <Field label="Telefono" value={agency.phone} />
        <Field label="Email" value={agency.email} />
        <Field label="Inizio monitoraggio compliance" value={agency.compliance_start} />
      </div>

      <div className="mt-5">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Territori di competenza</div>
        <div className="flex flex-wrap gap-1.5">
          {(agency.regions || []).map((r) => <Badge key={r} variant="secondary" className="text-[11px]">{r}</Badge>)}
          {(agency.provinces || []).map((p) => <Badge key={p} variant="outline" className="border-primary/40 text-[11px]">{p}</Badge>)}
          {(!agency.regions?.length && !agency.provinces?.length) && <span className="text-sm text-muted-foreground">Nessun territorio assegnato.</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-primary">
          {agency.is_direzione && <span className="inline-flex items-center gap-1"><Compass className="h-3.5 w-3.5" />Direzione (vede tutto)</span>}
          {agency.manages_direzionali && <span className="inline-flex items-center gap-1"><Compass className="h-3.5 w-3.5" />Gestisce i direzionali</span>}
          {agency.chiavi_in_mano && <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />Canale chiavi in mano</span>}
        </div>
      </div>

      {agency.notes && <div className="mt-4"><Field label="Note" value={agency.notes} /></div>}
      <div className="mt-5">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Referenti</div>
        {refs.length === 0 && <p className="text-sm text-muted-foreground">Nessun referente aggiuntivo.</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {refs.map((r, i) => (
            <div key={i} className="rounded-lg border border-border p-3" data-testid={`agency-referente-${i}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{r.nome || "—"}</span>
                {r.ruolo && <Badge variant="secondary" className="text-[10px]">{r.ruolo}</Badge>}
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                {r.telefono && <a href={`tel:${r.telefono}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3 w-3" />{r.telefono}</a>}
                {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3 w-3" />{r.email}</a>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="anagrafica-edit-dialog">
          <DialogHeader><DialogTitle>Modifica anagrafica agenzia</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            {ANA_FIELDS.map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs">{l}</Label>
                <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 h-9" data-testid={`ana-${k}`} />
              </div>
            ))}
          </div>

          <div className="mt-1 grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" />Sede (città)</Label>
              <Select value={form.sede_city || undefined} onValueChange={setSede}>
                <SelectTrigger className="mt-1 h-9" data-testid="ana-sede-city"><SelectValue placeholder="Seleziona la città sede" /></SelectTrigger>
                <SelectContent className="max-h-64">{ITALIAN_CITIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name} ({c.region})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs"><MapPin className="h-3 w-3" />Indirizzo sede di partenza</Label>
              <Input value={form.sede_op_address || ""} onChange={(e) => set("sede_op_address", e.target.value)} placeholder="Es. Via Roma 12" className="mt-1 h-9" data-testid="ana-sede-address" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">La sede di partenza è geolocalizzata al salvataggio (usata per i km del funnel visite).</p>

          <div>
            <Label className="text-xs">Inizio monitoraggio compliance</Label>
            <Input type="date" value={(form.compliance_start || "").slice(0, 10)} onChange={(e) => set("compliance_start", e.target.value || null)} className="mt-1 h-9 sm:w-1/2" data-testid="ana-compliance-start" />
            <p className="pt-1 text-[11px] text-muted-foreground">La Compliance parte da questa data (o dal 1° gennaio se successiva). Se vuota, usa la data di inizio agenzia.</p>
          </div>

          <div>
            <Label className="text-xs">Regioni di competenza</Label>
            <Select onValueChange={addRegion}>
              <SelectTrigger className="mt-1 h-9" data-testid="ana-region"><SelectValue placeholder="Aggiungi regione" /></SelectTrigger>
              <SelectContent>{regions.filter((r) => !(form.regions || []).includes(r) && !assignedRegionsOther.has(r)).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(form.regions || []).map((r) => (
                <Badge key={r} variant="secondary" className="gap-1">{r}<button onClick={() => removeRegion(r)}><X className="h-3 w-3" /></button></Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Province di competenza</Label>
            <Popover open={provOpen} onOpenChange={setProvOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="mt-1 w-full justify-between font-normal text-muted-foreground" data-testid="ana-province">
                  Aggiungi provincia <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Cerca provincia…" />
                  <CommandList className="max-h-72">
                    <CommandEmpty>Nessuna provincia disponibile.</CommandEmpty>
                    {Object.keys(provincesByRegion).sort().map((reg) => {
                      const opts = (provincesByRegion[reg] || []).filter((p) => !(form.provinces || []).includes(p) && !assignedProvincesOther.has(p));
                      if (opts.length === 0) return null;
                      return (
                        <CommandGroup key={reg} heading={reg}>
                          {opts.map((p) => (<CommandItem key={p} value={`${p} ${reg}`} onSelect={() => { addProvince(p); setProvOpen(false); }}>{p}</CommandItem>))}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="pt-1 text-[11px] text-muted-foreground">La provincia ha priorità sulla regione. I territori già assegnati ad altre agenzie non compaiono.</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(form.provinces || []).map((p) => (
                <Badge key={p} variant="outline" className="gap-1 border-primary/40">{p}<button onClick={() => removeProvince(p)}><X className="h-3 w-3" /></button></Badge>
              ))}
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="ana-direzione-wrap">
            <Checkbox checked={!!form.is_direzione} onCheckedChange={(v) => set("is_direzione", !!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed">
              <span className="flex items-center gap-1 font-semibold"><Compass className="h-3.5 w-3.5 text-primary" />Figura di Direzione (Responsabile / Head of Partners)</span>
              <span className="text-muted-foreground">Vede tutti i contatti e le agenzie e non diventa titolare dei contatti.</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="ana-direzionali-wrap">
            <Checkbox checked={!!form.manages_direzionali} onCheckedChange={(v) => set("manages_direzionali", !!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed">
              <span className="flex items-center gap-1 font-semibold"><Compass className="h-3.5 w-3.5 text-primary" />Gestisce clienti e prospect direzionali</span>
              <span className="text-muted-foreground">Vede anche i contatti senza agenzia di riferimento (direzionali), oltre ai propri territori.</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="ana-chiavi-wrap">
            <Checkbox checked={!!form.chiavi_in_mano} onCheckedChange={(v) => set("chiavi_in_mano", !!v)} className="mt-0.5" />
            <span className="text-xs leading-relaxed">
              <span className="flex items-center gap-1 font-semibold"><KeyRound className="h-3.5 w-3.5 text-primary" />Evidenzia nel canale chiavi in mano</span>
              <span className="text-muted-foreground">L'agenzia diventa visibile su configuratore.torri.solar (API pubblica /public/agencies).</span>
            </span>
          </label>

          <div>
            <Label className="text-xs">Note</Label>
            <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1" />
          </div>

          <div className="mt-2">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs">Referenti</Label>
              <Button size="sm" variant="outline" onClick={addRef} data-testid="add-referente"><Plus className="mr-1 h-3.5 w-3.5" />Aggiungi</Button>
            </div>
            <div className="space-y-2">
              {(form.referenti || []).map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-center gap-2">
                  <Input placeholder="Nome" value={r.nome || ""} onChange={(e) => setRef(i, "nome", e.target.value)} className="h-8" />
                  <Input placeholder="Ruolo" value={r.ruolo || ""} onChange={(e) => setRef(i, "ruolo", e.target.value)} className="h-8" />
                  <Input placeholder="Telefono" value={r.telefono || ""} onChange={(e) => setRef(i, "telefono", e.target.value)} className="h-8" />
                  <Input placeholder="Email" value={r.email || ""} onChange={(e) => setRef(i, "email", e.target.value)} className="h-8" />
                  <Button size="icon" variant="ghost" onClick={() => delRef(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-anagrafica">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
